import React, { useEffect, useRef, useState } from 'react';
import { TreePine, Ruler, FileText, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';

const iconMap = {
  TreePine: TreePine,
  Ruler: Ruler,
  FileText: FileText,
  Award: Award,
};

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  const services = t('servicesList', []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="pakalpojumi" className="py-24 relative overflow-hidden section-moss" ref={sectionRef} data-testid="services-section">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#1F3D2B]/8 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#1F3D2B]/6 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      <div className="absolute top-16 left-1/4 w-40 h-2 forest-branch animate-sway" style={{ animationDelay: '1.5s' }} />
      
      {/* Forest pattern overlay */}
      <div className="absolute inset-0 forest-pattern opacity-30" />
      <div className="absolute inset-0 forest-grain opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center sm:text-left mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-testid="services-header">
            <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4 px-4 py-1 bg-[#1F3D2B]/10 rounded-full" data-testid="services-eyebrow">
              {t('services.eyebrow', 'Mūsu pakalpojumi')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-testid="services-title">
              {t('services.title', 'Profesionāli mežsaimniecības pakalpojumi meža īpašniekiem')}
            </h2>
            <p className="text-[#2E2E2E]/70 text-lg max-w-3xl mx-auto sm:mx-0" data-testid="services-description">
              {t('services.description', '')}
            </p>
            <p className="text-[#2E2E2E]/70 text-base max-w-3xl mx-auto sm:mx-0 mt-4" data-testid="services-mission">
              {t('services.mission', '')}
            </p>
            
            {/* Decorative line */}
            <div className="mt-8 flex items-center justify-center sm:justify-start gap-2">
              <div className="w-12 h-0.5 bg-[#1F3D2B]/20 rounded" />
              <TreePine className="w-5 h-5 text-[#1F3D2B]/40" />
              <div className="w-12 h-0.5 bg-[#1F3D2B]/20 rounded" />
            </div>
          </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className={`group border border-gray-100 shadow-sm hover:shadow-2xl hover:border-[#1F3D2B]/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden forest-panel card-shine ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                data-testid={`service-card-${service.id}`}
              >
                {/* Card top accent */}
                <div className="h-1 bg-gradient-to-r from-[#1F3D2B] to-[#3d6b4a] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1F3D2B] group-hover:scale-110 transition-all duration-500">
                      {IconComponent ? (
                        <IconComponent className="w-7 h-7 text-[#1F3D2B] group-hover:text-white transition-colors duration-500" />
                      ) : (
                        <TreePine className="w-7 h-7 text-[#1F3D2B] group-hover:text-white transition-colors duration-500" />
                      )}
                    </div>
                    <CardTitle className="text-xl text-[#2E2E2E] leading-tight group-hover:text-[#1F3D2B] transition-colors duration-300" data-testid={`service-title-${service.id}`}>
                      {service.category}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-[#2E2E2E]/70 text-sm mb-4 leading-relaxed" data-testid={`service-description-${service.id}`}>
                    {service.description}
                  </p>
                  <ul className="space-y-2" data-testid={`service-items-${service.id}`}>
                    {service.items?.map((item, idx) => (
                      <li key={item} className="flex items-start gap-3 group/item" data-testid={`service-item-${service.id}-${idx}`}>
                        <div className="w-2 h-2 rounded-full bg-[#1F3D2B]/40 mt-2 flex-shrink-0 group-hover/item:bg-[#1F3D2B] group-hover/item:scale-125 transition-all duration-300" />
                        <span className="text-[#2E2E2E]/80 text-sm group-hover/item:text-[#2E2E2E] transition-colors duration-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-block p-6 rounded-2xl forest-panel relative overflow-hidden" data-testid="services-cta-panel">
            <img
              src={companyInfo.logo.transparent}
              alt={companyInfo.name}
              className="absolute -right-8 -bottom-10 w-52 opacity-10 pointer-events-none select-none"
              data-testid="services-logo-watermark"
            />
            <p className="text-[#2E2E2E]/70 mb-4" data-testid="services-cta-text">
              {t('services.ctaQuestion', 'Neesat pārliecināti, kāds pakalpojums jums nepieciešams?')}
            </p>
            <a
              href="#kontakti"
              className="inline-flex items-center justify-center px-6 py-3 forest-cta font-semibold transition-all duration-300 group"
              data-testid="services-cta-link"
            >
              <span className="relative text-white">
                {t('services.ctaLink', 'Sazinieties ar mums bezmaksas konsultācijai')}
              </span>
              <svg className="w-4 h-4 ml-2 text-white group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
